import { motion } from "framer-motion";
import { useEnquiry } from "@/components/site/EnquiryProvider";

export default function Exhibition({ artworks = [] }) {
  const { openEnquiry } = useEnquiry();

  return (
    <section
      id="exhibition"
      className="relative py-24 md:py-36 bg-ink text-paper overflow-hidden"
      data-testid="exhibition-section"
    >
      {/* faint oversized word */}
      <span className="pointer-events-none select-none absolute -top-4 md:top-6 right-0 font-serif italic text-[22vw] leading-none text-paper/[0.04] whitespace-nowrap">
        Kenya
      </span>

      <div className="max-w-screen-2xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 lg:gap-16 relative">
        {/* Sticky story */}
        <div className="md:col-span-4">
          <div className="md:sticky md:top-28">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-body text-[11px] tracking-[0.35em] uppercase text-ochre mb-5"
            >
              Featured Exhibition
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
            >
              Framing Flowers
              <span className="block italic text-ochre text-3xl sm:text-4xl mt-2">
                Spirit of Kenya
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-8 font-serif italic text-xl md:text-2xl text-paper/80 leading-relaxed"
            >
              &ldquo;The roses bleed their colour onto a grey backdrop &mdash;
              black and white bright stripes against a wet green breezy
              landscape.&rdquo;
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="mt-6 font-body text-sm md:text-base text-paper/60 leading-relaxed max-w-sm"
            >
              A solo series of paintings and painterly photographs born of the
              flower markets, wildlife and endearing people of Kenya. Enquire for
              availability of any work.
            </motion.p>
            <div className="mt-8 font-body text-[11px] tracking-[0.2em] uppercase text-paper/50">
              Paintings &amp; Fine-art Photographs
            </div>
          </div>
        </div>

        {/* Masonry gallery */}
        <div className="md:col-span-8">
          <div className="columns-2 lg:columns-3 gap-4 lg:gap-6 [column-fill:_balance]">
            {artworks.map((art, i) => (
              <motion.button
                key={art.slug}
                type="button"
                onClick={() => openEnquiry(art)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.06 }}
                data-testid={`exhibition-item-${art.slug}`}
                data-cursor="Enquire"
                className="group mb-4 lg:mb-6 block w-full break-inside-avoid text-left"
              >
                <div className="relative overflow-hidden bg-brown/40">
                  <img
                    src={art.image}
                    alt={art.title}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.07]"
                  />
                  <div className="absolute inset-0 flex items-end bg-gradient-to-t from-ink/85 via-ink/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    <div className="p-4">
                      <p className="font-serif text-lg text-paper leading-tight">
                        {art.title}
                      </p>
                      <p className="font-body text-[10px] tracking-[0.15em] uppercase text-ochre mt-1">
                        {art.kind} &middot; Enquire
                      </p>
                    </div>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
